/**
 * useRealtimeAudio Hook
 * Handles real-time audio streaming with OpenAI Realtime API via WebSocket
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { apiRequest } from '@/lib/queryClient';

export interface RealtimeAudioConfig {
    restaurantId: string;
    tableId?: string;
    tableNumber?: string;
    customerSessionId?: string;
    restaurantName?: string;
    restaurantState?: string;
    restaurantDescription?: string;
    restaurantHours?: string;
    restaurantPhone?: string;
    restaurantAddress?: string;
    menuItems?: any[];
    restaurantKnowledge?: {
        story?: string;
        philosophy?: string;
        sourcingPractices?: string;
        specialTechniques?: string;
        awards?: string;
        sustainabilityPractices?: string;
    } | null;
    faqKnowledge?: Array<{
        question: string;
        answer: string;
    }>;
    currentCart?: Array<{
        id: string;
        name: string;
        price: string;
        quantity: number;
        customerNote?: string;
        allergies?: string;
    }>;
    language?: string;
    onAddToCart?: (item: any) => void;
    onRemoveFromCart?: (item: any) => void;
    onOrderConfirmed?: (order: any) => void;  // Called when order is placed via AI
    onChefCalled?: (message: string) => void; // Called when AI calls the chef
    onWaiterCalled?: (message: string) => void; // Called when AI calls the waiter
    onCheckout?: (tipAmount?: number, customerNote?: string) => void; // Called when AI triggers checkout page
    onTranscript?: (text: string, role: 'user' | 'assistant') => void;
    onError?: (error: string) => void;
    onStatusChange?: (status: RealtimeStatus) => void;
    sessionType?: 'waiter' | 'interviewer';
    interviewConfig?: {
        type: 'menu_item' | 'restaurant';
        itemName?: string;
        itemDescription?: string;
        existingKnowledge?: any;
    };
}

export type RealtimeStatus =
    | 'disconnected'
    | 'connecting'
    | 'connected'
    | 'listening'
    | 'processing'
    | 'speaking';



// PCM16 Audio utilities
const floatTo16BitPCM = (float32Array: Float32Array): ArrayBuffer => {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < float32Array.length; i++) {
        const s = Math.max(-1, Math.min(1, float32Array[i]));
        view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return buffer;
};

const pcm16ToFloat32 = (pcm16: ArrayBuffer): Float32Array => {
    const view = new DataView(pcm16);
    const float32 = new Float32Array(pcm16.byteLength / 2);
    for (let i = 0; i < float32.length; i++) {
        const int16 = view.getInt16(i * 2, true);
        // Use consistent normalization to prevent asymmetric clipping
        float32[i] = int16 / 32768;
    }
    return float32;
};

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
};

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
};

export function useRealtimeAudio(config: RealtimeAudioConfig) {
    const [status, setStatus] = useState<RealtimeStatus>('disconnected');
    const [isSessionActive, setIsSessionActive] = useState(false);

    const wsRef = useRef<WebSocket | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const processorRef = useRef<ScriptProcessorNode | null>(null);
    const playbackQueueRef = useRef<Float32Array[]>([]);
    const isPlayingRef = useRef(false);
    const nextPlayTimeRef = useRef(0);
    const statusRef = useRef<RealtimeStatus>('disconnected');
    const currentSourceRef = useRef<AudioBufferSourceNode | null>(null);
    const gainNodeRef = useRef<GainNode | null>(null);

    // Inactivity timeout tracking
    const inactivityTimeoutRef = useRef<any>(null);
    const INACTIVITY_TIMEOUT_MS = 20000; // 20 seconds
    const lastActivityTimeRef = useRef<number>(Date.now());

    // AI Usage tracking
    const usageTimerRef = useRef<any>(null);
    const lastReportTimeRef = useRef<number>(0);

    // Update status and notify
    const updateStatus = useCallback((newStatus: RealtimeStatus) => {
        setStatus(newStatus);
        statusRef.current = newStatus;
        config.onStatusChange?.(newStatus);
    }, [config]);

    // Reset inactivity timer
    const resetActivityTimer = useCallback(() => {
        lastActivityTimeRef.current = Date.now();

        // Clear existing timeout
        if (inactivityTimeoutRef.current) {
            clearTimeout(inactivityTimeoutRef.current);
        }

        // Only set timeout if we're in an active state
        if (statusRef.current !== 'disconnected' && statusRef.current !== 'connecting') {
            inactivityTimeoutRef.current = setTimeout(() => {
                const timeSinceLastActivity = Date.now() - lastActivityTimeRef.current;

                // Double-check we're still inactive
                if (timeSinceLastActivity >= INACTIVITY_TIMEOUT_MS) {
                    console.log('[RealtimeAudio] Auto-disconnecting due to 30s inactivity');
                    config.onError?.('Session ended due to inactivity');

                    // Disconnect the session
                    if (wsRef.current) {
                        if (wsRef.current.readyState === WebSocket.OPEN) {
                            wsRef.current.send(JSON.stringify({ type: 'end_session' }));
                        }
                        if (wsRef.current.readyState !== WebSocket.CLOSED) {
                            wsRef.current.close();
                        }
                        wsRef.current = null;
                    }

                    // Clean up audio resources
                    if (processorRef.current) {
                        processorRef.current.disconnect();
                        processorRef.current = null;
                    }

                    if (mediaStreamRef.current) {
                        mediaStreamRef.current.getTracks().forEach(track => track.stop());
                        mediaStreamRef.current = null;
                    }

                    if (audioContextRef.current) {
                        audioContextRef.current.close();
                        audioContextRef.current = null;
                    }

                    playbackQueueRef.current = [];
                    isPlayingRef.current = false;

                    updateStatus('disconnected');
                    setIsSessionActive(false);
                }
            }, INACTIVITY_TIMEOUT_MS);
        }
    }, [config, updateStatus, INACTIVITY_TIMEOUT_MS]);

    // Clear inactivity timer
    const clearActivityTimer = useCallback(() => {
        if (inactivityTimeoutRef.current) {
            clearTimeout(inactivityTimeoutRef.current);
            inactivityTimeoutRef.current = null;
        }
    }, []);

    // Play audio from the queue
    const playNextAudioChunk = useCallback(() => {
        if (!audioContextRef.current || playbackQueueRef.current.length === 0) {
            isPlayingRef.current = false;
            // Use statusRef.current to avoid stale closure issues
            if (playbackQueueRef.current.length === 0 && statusRef.current === 'speaking') {
                // If microphone is still active, go back to listening for continuous conversation
                // Otherwise, go to connected state
                if (processorRef.current && mediaStreamRef.current) {
                    updateStatus('listening');
                    console.log('[RealtimeAudio] AI finished speaking, resuming listening');
                } else {
                    updateStatus('connected');
                    console.log('[RealtimeAudio] AI finished speaking, mic inactive');
                }
            }
            return;
        }

        isPlayingRef.current = true;
        const chunk = playbackQueueRef.current.shift()!;

        const buffer = audioContextRef.current.createBuffer(1, chunk.length, 24000);
        buffer.getChannelData(0).set(chunk);

        const source = audioContextRef.current.createBufferSource();
        source.buffer = buffer;

        // Create gain node for volume control (prevents clipping/distortion)
        if (!gainNodeRef.current) {
            gainNodeRef.current = audioContextRef.current.createGain();
            gainNodeRef.current.gain.value = 0.7; // 70% volume to prevent crackling
            gainNodeRef.current.connect(audioContextRef.current.destination);
        }

        source.connect(gainNodeRef.current);
        currentSourceRef.current = source;

        const currentTime = audioContextRef.current.currentTime;
        const startTime = Math.max(currentTime, nextPlayTimeRef.current);
        source.start(startTime);

        nextPlayTimeRef.current = startTime + buffer.duration;

        source.onended = () => {
            if (currentSourceRef.current === source) {
                currentSourceRef.current = null;
            }
            playNextAudioChunk();
        };
    }, [updateStatus]);

    // Listen for language or cart changes and update session config
    useEffect(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            const configUpdate: any = {};
            let shouldUpdate = false;

            if (config.language) {
                configUpdate.language = config.language;
                shouldUpdate = true;
            }

            if (config.currentCart) {
                configUpdate.currentCart = config.currentCart;
                shouldUpdate = true;
            }

            if (shouldUpdate) {
                console.log('[RealtimeAudio] Sending session config update (language/cart)');
                wsRef.current.send(JSON.stringify({
                    type: 'update_session_config',
                    config: configUpdate
                }));
            }
        }
    }, [config.language, config.currentCart]);

    // Handle incoming WebSocket messages
    const handleMessage = useCallback((event: MessageEvent) => {
        try {
            const message = JSON.parse(event.data);

            // Reset inactivity timer on any message (indicates activity)
            resetActivityTimer();

            switch (message.type) {
                case 'session_started':
                    console.log('[RealtimeAudio] Session started');
                    updateStatus('connected');
                    resetActivityTimer(); // Start the inactivity timer
                    break;

                case 'audio':
                    // Received audio from AI
                    if (status !== 'speaking') {
                        updateStatus('speaking');
                    }

                    // Decode and queue audio
                    const pcmData = base64ToArrayBuffer(message.audio);
                    const floatData = pcm16ToFloat32(pcmData);
                    playbackQueueRef.current.push(floatData);

                    // Check if AudioContext needs to be created or recreated
                    // It might be null, or in 'closed' state after a previous disconnect
                    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
                        try {
                            console.log('[RealtimeAudio] Creating new AudioContext (previous was null or closed)');
                            audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
                            // Clear gain node reference since we have a new context
                            gainNodeRef.current = null;
                        } catch (e) {
                            console.error('[RealtimeAudio] Failed to create AudioContext:', e);
                        }
                    }

                    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                        audioContextRef.current.resume();
                    }

                    if (!isPlayingRef.current) {
                        playNextAudioChunk();
                    }
                    break;

                case 'transcript':
                    // User or AI transcript - indicates activity
                    config.onTranscript?.(message.transcript, message.role);
                    break;

                case 'response_done':
                    console.log('[RealtimeAudio] Response complete');
                    // Status will be updated when audio queue is empty
                    break;

                case 'add_to_cart':
                    console.log('[RealtimeAudio] Add to cart:', message.item);
                    config.onAddToCart?.(message.item);
                    break;

                case 'remove_from_cart':
                    console.log('[RealtimeAudio] Remove from cart:', message.item);
                    config.onRemoveFromCart?.(message.item);
                    break;

                case 'order_confirmed':
                    console.log('[RealtimeAudio] Order confirmed:', message.order);
                    config.onOrderConfirmed?.(message.order);
                    break;

                case 'chef_called':
                    console.log('[RealtimeAudio] Chef called:', message.message);
                    config.onChefCalled?.(message.message || message.question);
                    break;

                case 'waiter_called':
                    console.log('[RealtimeAudio] Waiter called:', message.message);
                    config.onWaiterCalled?.(message.message);
                    break;

                case 'checkout':
                    console.log('[RealtimeAudio] Opening checkout page', { tipAmount: message.tipAmount, customerNote: message.customerNote });
                    config.onCheckout?.(message.tipAmount, message.customerNote);
                    break;

                case 'interruption':
                    // Stop playing current AI audio immediately
                    console.log('[RealtimeAudio] Interruption signal received');
                    if (currentSourceRef.current) {
                        try {
                            currentSourceRef.current.stop();
                        } catch (e) {
                            // Ignore
                        }
                        currentSourceRef.current = null;
                    }
                    playbackQueueRef.current = [];
                    isPlayingRef.current = false;
                    nextPlayTimeRef.current = 0;
                    // Note: We don't change status to 'connected' here because we want to keep 'listening'
                    break;



                case 'error':
                    // Ignore harmless cancellation race conditions
                    if (message.error?.includes('no active response found')) {
                        console.log('[RealtimeAudio] Ignored harmless response.cancel mismatch error');
                        break;
                    }
                    console.error('[RealtimeAudio] Error:', message.error);
                    config.onError?.(message.error);
                    break;

                case 'session_ended':
                    console.log('[RealtimeAudio] Session ended');
                    clearActivityTimer(); // Clear timer when session ends
                    updateStatus('disconnected');
                    setIsSessionActive(false);
                    break;

                case 'pong':
                    // Heartbeat response
                    break;

                default:
                    console.log('[RealtimeAudio] Unknown message:', message.type);
            }
        } catch (error) {
            console.error('[RealtimeAudio] Error parsing message:', error);
        }
    }, [config, status, updateStatus, playNextAudioChunk, resetActivityTimer, clearActivityTimer]);

    // Connect to WebSocket server
    const connect = useCallback(async (retryCount = 0) => {
        const MAX_RETRIES = 3;

        if (wsRef.current?.readyState === WebSocket.OPEN) {
            console.log('[RealtimeAudio] Already connected');
            return;
        }

        // Initialize AudioContext on user gesture
        if (!audioContextRef.current) {
            try {
                audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
            } catch (e) {
                console.error('[RealtimeAudio] Failed to create AudioContext during connect:', e);
            }
        } else if (audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
        }

        // Reset play time tracker for new connection
        nextPlayTimeRef.current = 0;
        playbackQueueRef.current = [];

        updateStatus('connecting');

        // Get WebSocket URL
        const wsBaseUrl = (import.meta as any).env.VITE_REALTIME_WEBSOCKET_URL
            || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                ? 'ws://localhost:3005/ws/realtime'
                : `wss://${window.location.host}/ws/realtime`);

        let wsUrl = `${wsBaseUrl}?restaurantId=${config.restaurantId}`;
        if (config.tableId) {
            wsUrl += `&tableId=${config.tableId}`;
        }
        if (config.tableNumber) {
            wsUrl += `&tableNumber=${config.tableNumber}`;
        }
        if (config.customerSessionId) {
            wsUrl += `&customerSessionId=${config.customerSessionId}`;
        }

        // Extract HTTP URL from WebSocket URL for health check
        const httpUrl = wsBaseUrl.replace('wss://', 'https://').replace('ws://', 'http://').replace('/ws/realtime', '');

        console.log('[RealtimeAudio] Connecting to:', wsUrl);

        // Wake up the server first (for Render free tier spin-up)
        if (retryCount === 0) {
            try {
                console.log('[RealtimeAudio] Waking up server...');
                const healthCheck = await fetch(`${httpUrl}/health`, {
                    method: 'GET',
                    mode: 'cors',
                }).catch(() => null);

                if (healthCheck?.ok) {
                    console.log('[RealtimeAudio] Server is awake');
                } else {
                    console.log('[RealtimeAudio] Server might be spinning up, continuing...');
                }
            } catch (e) {
                console.log('[RealtimeAudio] Health check failed, will try WebSocket anyway');
            }
        }

        try {
            wsRef.current = new WebSocket(wsUrl);

            // Set connection timeout
            const connectionTimeout = setTimeout(() => {
                if (wsRef.current?.readyState === WebSocket.CONNECTING) {
                    console.log('[RealtimeAudio] Connection timeout, retrying...');
                    wsRef.current.close();

                    if (retryCount < MAX_RETRIES) {
                        setTimeout(() => connect(retryCount + 1), 2000);
                    } else {
                        config.onError?.('Connection timeout - server may be starting up, please try again');
                        updateStatus('disconnected');
                    }
                }
            }, 15000); // 15 second timeout

            wsRef.current.onopen = () => {
                clearTimeout(connectionTimeout);
                console.log('[RealtimeAudio] WebSocket connected successfully');

                // Start session with restaurant info for the AI
                wsRef.current?.send(JSON.stringify({
                    type: 'start_session',
                    language: config.language || 'en',
                    restaurantName: config.restaurantName || 'Restaurant',
                    restaurantState: config.restaurantState,
                    restaurantDescription: config.restaurantDescription,
                    restaurantHours: config.restaurantHours,
                    restaurantPhone: config.restaurantPhone,
                    restaurantAddress: config.restaurantAddress,
                    menuItems: config.menuItems || [],
                    restaurantKnowledge: config.restaurantKnowledge,
                    faqKnowledge: config.faqKnowledge,
                    currentCart: config.currentCart || [],
                    sessionType: config.sessionType || 'waiter',
                    interviewConfig: config.interviewConfig,
                }));
            };

            wsRef.current.onmessage = handleMessage;

            wsRef.current.onerror = (error) => {
                clearTimeout(connectionTimeout);
                console.error('[RealtimeAudio] WebSocket error:', error);

                // Retry on error
                if (retryCount < MAX_RETRIES) {
                    console.log(`[RealtimeAudio] Retrying (${retryCount + 1}/${MAX_RETRIES})...`);
                    setTimeout(() => connect(retryCount + 1), 2000);
                } else {
                    console.error('[RealtimeAudio] Max retries reached. Server URL:', wsUrl);
                    config.onError?.('Connection failed after retries - please try again');
                    updateStatus('disconnected');
                }
            };

            wsRef.current.onclose = (event) => {
                clearTimeout(connectionTimeout);
                console.log('[RealtimeAudio] WebSocket closed:', event.code, event.reason);
                updateStatus('disconnected');
                setIsSessionActive(false);
            };
        } catch (error) {
            console.error('[RealtimeAudio] Failed to connect:', error);
            config.onError?.('Failed to connect');
            updateStatus('disconnected');
        }
    }, [config, handleMessage, updateStatus]);

    // Start listening (capture microphone and stream to server)
    const startListening = useCallback(async () => {
        if (status !== 'connected' && status !== 'speaking') {
            console.warn('[RealtimeAudio] Cannot start listening - not connected');
            return;
        }

        try {
            // Request microphone access
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: 24000,
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    // @ts-ignore - non-standard constraint
                    typingNoiseSuppression: true,
                },
            });

            mediaStreamRef.current = stream;

            // Create audio context for capturing if not already created
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
            } else if (audioContextRef.current.state === 'suspended') {
                await audioContextRef.current.resume();
            }

            const source = audioContextRef.current.createMediaStreamSource(stream);

            // Use ScriptProcessorNode for capturing audio
            // Note: This is deprecated but AudioWorklet requires more setup
            const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);
            processorRef.current = processor;

            processor.onaudioprocess = (event) => {
                if (wsRef.current?.readyState !== WebSocket.OPEN) return;

                // Allow sending audio even when status is 'speaking' or 'processing' for barge-in capability
                // Explicitly check we are not disconnected
                if (statusRef.current === 'disconnected') return;

                const inputData = event.inputBuffer.getChannelData(0);
                const pcmData = floatTo16BitPCM(inputData);
                const base64Audio = arrayBufferToBase64(pcmData);

                wsRef.current.send(JSON.stringify({
                    type: 'audio',
                    audio: base64Audio,
                }));
            };

            source.connect(processor);
            processor.connect(audioContextRef.current.destination);

            updateStatus('listening');
            setIsSessionActive(true);

            // Reset inactivity timer when user starts listening
            resetActivityTimer();

            // Ask Lelah to say a short prompt when starting to listen
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'request_prompt' }));
            }

            console.log('[RealtimeAudio] Started listening');
        } catch (error) {
            console.error('[RealtimeAudio] Failed to start listening:', error);
            config.onError?.('Failed to access microphone');
        }
    }, [config, status, updateStatus, resetActivityTimer]);

    // Stop listening
    const stopListening = useCallback(() => {
        console.log('[RealtimeAudio] Stop listening requested');

        // Stop the microphone
        if (processorRef.current) {
            processorRef.current.disconnect();
            processorRef.current = null;
        }

        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }

        if (status === 'listening') {
            // Cancel any pending response and clear the audio buffer
            // This prevents Lelah from saying anything when user taps to stop
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'response.cancel' }));
                wsRef.current.send(JSON.stringify({ type: 'input_audio_buffer.clear' }));
            }

            // Go back to connected state (ready but not listening)
            updateStatus('connected');

            // Reset inactivity timer when user stops listening (user interaction)
            resetActivityTimer();
        }

        console.log('[RealtimeAudio] Stopped listening - buffer cleared, no response triggered');
    }, [status, updateStatus, resetActivityTimer]);

    // Initialize audio (must be called from user gesture)
    const initAudio = useCallback(async () => {
        if (!audioContextRef.current) {
            try {
                audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
                console.log('[RealtimeAudio] AudioContext initialized');
            } catch (e) {
                console.error('[RealtimeAudio] Failed to create AudioContext:', e);
            }
        }

        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume();
            console.log('[RealtimeAudio] AudioContext resumed');
        }
    }, []);

    // Interrupt AI response AND stop listening completely
    const interrupt = useCallback(() => {
        console.log('[RealtimeAudio] Interrupting - stopping everything');

        // Stop current audio immediately
        if (currentSourceRef.current) {
            try {
                currentSourceRef.current.stop();
            } catch (e) {
                // Ignore if already stopped
            }
            currentSourceRef.current = null;
        }

        // Clear playback queue
        playbackQueueRef.current = [];
        isPlayingRef.current = false;
        nextPlayTimeRef.current = 0;

        // Stop the microphone completely
        if (processorRef.current) {
            processorRef.current.disconnect();
            processorRef.current = null;
        }

        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }

        // Tell server to cancel any pending response
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'cancel' }));
            // Also clear the audio buffer on server side
            wsRef.current.send(JSON.stringify({ type: 'input_audio_buffer.clear' }));
        }

        updateStatus('connected');
        console.log('[RealtimeAudio] Fully interrupted - mic off, audio stopped');
    }, [updateStatus]);

    // Disconnect
    const disconnect = useCallback(() => {
        // Clear inactivity timer
        clearActivityTimer();

        stopListening();

        if (wsRef.current) {
            // Send final usage report before closing
            if (lastReportTimeRef.current > 0) {
                const deltaSeconds = Math.round((Date.now() - lastReportTimeRef.current) / 1000);
                if (deltaSeconds > 0) {
                    console.log(`[RealtimeAudio] Final reporting ${deltaSeconds}s usage via apiRequest`);

                    apiRequest('POST', '/api/analytics/ai-usage/time', {
                        restaurantId: config.restaurantId,
                        seconds: deltaSeconds,
                        customerSessionId: config.customerSessionId
                    }).catch(err => console.warn('Error reporting final OpenAI usage:', err));
                }
                lastReportTimeRef.current = 0;
            }

            // Only send end_session if the connection is actually open
            if (wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'end_session' }));
            }
            // Close regardless of state (handles CONNECTING state too)
            if (wsRef.current.readyState !== WebSocket.CLOSED) {
                wsRef.current.close();
            }
            wsRef.current = null;
        }

        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }

        // Clean up gain node
        gainNodeRef.current = null;

        playbackQueueRef.current = [];
        isPlayingRef.current = false;

        updateStatus('disconnected');
        setIsSessionActive(false);

        console.log('[RealtimeAudio] Disconnected');
    }, [stopListening, updateStatus, clearActivityTimer]);

    // Store disconnect in a ref to avoid cleanup issues
    const disconnectRef = useRef(disconnect);
    disconnectRef.current = disconnect;

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            disconnectRef.current();
        };
    }, []);

    // Track conversation usage time (seconds)
    // IMPORTANT: Only bill when user is ACTIVELY interacting (listening, processing, speaking)
    // Do NOT bill for 'connected' status (pre-connection idle time)
    useEffect(() => {
        const isActivelyInteracting = status === 'listening' || status === 'processing' || status === 'speaking';

        if (isActivelyInteracting) {
            if (!usageTimerRef.current) {
                console.log('[RealtimeAudio] Starting usage timer (user is actively interacting)');
                lastReportTimeRef.current = Date.now();
                usageTimerRef.current = setInterval(() => {
                    const now = Date.now();
                    const deltaSeconds = Math.round((now - lastReportTimeRef.current) / 1000);

                    if (deltaSeconds >= 20) {
                        console.log(`[RealtimeAudio] Reporting ${deltaSeconds}s usage via apiRequest`);

                        apiRequest('POST', '/api/analytics/ai-usage/time', {
                            restaurantId: config.restaurantId,
                            seconds: deltaSeconds,
                            customerSessionId: config.customerSessionId
                        })
                            .then(() => {
                                // Only advance the cursor if the request succeeded
                                lastReportTimeRef.current = now;
                            })
                            .catch(err => {
                                console.warn('Error reporting OpenAI usage (will retry next cycle):', err);
                                // Do NOT update lastReportTimeRef, so deltaSeconds will accumulate and include these seconds next time
                            });
                    }
                }, 10 * 1000); // Check every 10 seconds
            }
        } else if (status === 'disconnected' || status === 'connected' || status === 'connecting') {
            // Stop timer when not actively interacting (including idle 'connected' state)
            if (usageTimerRef.current) {
                // Report any remaining time before stopping
                if (lastReportTimeRef.current > 0) {
                    const now = Date.now();
                    const deltaSeconds = Math.round((now - lastReportTimeRef.current) / 1000);
                    if (deltaSeconds > 0) {
                        console.log(`[RealtimeAudio] Reporting final ${deltaSeconds}s before stopping timer`);
                        apiRequest('POST', '/api/analytics/ai-usage/time', {
                            restaurantId: config.restaurantId,
                            seconds: deltaSeconds,
                            customerSessionId: config.customerSessionId
                        }).catch(err => console.warn('Error reporting final usage:', err));
                    }
                }
                console.log('[RealtimeAudio] Stopping usage timer (user stopped interacting)');
                clearInterval(usageTimerRef.current);
                usageTimerRef.current = null;
            }
            lastReportTimeRef.current = 0;
        }

        // We specifically DON'T clear the timer in the cleanup function 
        // to prevent resets when status changes from listening/speaking/processing
    }, [status]);

    // Heartbeat
    useEffect(() => {
        if (status === 'disconnected') return;

        const interval = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'ping' }));
            }
        }, 20000);

        return () => clearInterval(interval);
    }, [status]);

    return {
        status,
        isSessionActive,
        connect,
        initAudio,
        startListening,
        stopListening,
        interrupt,
        disconnect,
    };
}
